import { SupabaseClient } from '@supabase/supabase-js';
import {
  Card,
  ReviewLog,
  UserKnownWord,
  isTranslationLanguage,
  mergeCardNotes,
  mergeTranslations,
  sameCardNotes,
} from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { MAX_KNOWN_WORDS_PER_OP, SyncOpBody, syncKeys } from '../storage/outbox.js';
import { dedupeCards } from '../utils/dedupeCards.js';
import {
  cardToRow,
  reviewLogToRow,
  rowToCard,
  rowToKnownWord,
  rowToReviewLog,
  sameTranslations,
} from './cardRows.js';
import { migrateBundledPictures } from './deckPictureMigration.js';
import { signedInSupabase } from './syncTransport.js';

/**
 * Moving an installation over to "the cloud is the truth" (Docs/SYNC_CONTRACT.md, section 5).
 *
 * Up to v3.1.0 the device held the real copy of a learner's cards, known words and review
 * history and merged it with the cloud. That merge runs here ONE LAST TIME per learner and
 * device, so nothing that only this device has is lost: what the server lacks is uploaded, and of
 * two copies of a card the newer one wins. After that the server's copy is the only real one: the
 * device forgets where its last pull was, fetches everything again, and from then on only sends
 * operations (services/syncEngine.ts). A mark on the device says the move is done; a device with
 * the mark never runs this merge again.
 *
 * Nothing else may use this merge. It writes whole rows straight into the tables, the way the
 * old extension did, which is exactly what the new sync replaces.
 */

type Row = Record<string, unknown>;

/** The old extension's direct access to the learner's own rows. */
export interface LegacyRemote {
  fetchCards(): Promise<Row[]>;
  fetchKnownWords(): Promise<Row[]>;
  fetchReviewLogs(): Promise<Row[]>;
  /** The learner's profile row, or null when there is none. */
  fetchProfile(): Promise<{ onboarding_completed_at?: string | null } | null>;
  /** Whole-row upserts. An error the database itself raised carries its `code` (SQLSTATE). */
  upsertCards(rows: Row[]): Promise<void>;
  upsertReviewLogs(rows: Row[]): Promise<void>;
  deleteCards(ids: string[]): Promise<void>;
}

const BATCH_SIZE = 200;
/** PostgREST answers at most this many rows at a time. */
const PAGE_SIZE = 1000;

/** Set by v3.1.0 while a settings change had not reached the server yet. */
export const NATIVE_LANGUAGE_UNSYNCED_KEY = 'lingoping_native_language_unsynced';
export const NEW_WORDS_UNSYNCED_KEY = 'lingoping_new_words_per_day_unsynced';

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function failure(what: string, error: { message: string; code?: string }): Error {
  return Object.assign(new Error(`${what}: ${error.message}`), { code: error.code });
}

/** The real `LegacyRemote`: the learner's tables through the Supabase client. */
export function supabaseLegacyRemote(supabase: SupabaseClient, userId: string): LegacyRemote {
  /** Every row of the learner, read page by page (one answer holds at most 1000). */
  async function all(table: string): Promise<Row[]> {
    const rows: Row[] = [];
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('user_id', userId)
        .order('id')
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw failure(`Reading ${table} failed`, error);
      rows.push(...(data ?? []));
      if ((data ?? []).length < PAGE_SIZE) return rows;
    }
  }
  async function upsert(table: string, rows: Row[]): Promise<void> {
    const { error } = await supabase.from(table).upsert(rows, { onConflict: 'id' });
    if (error) throw failure(`Uploading ${table} failed`, error);
  }
  return {
    fetchCards: () => all('cards'),
    fetchKnownWords: () => all('user_known_words'),
    fetchReviewLogs: () => all('review_logs'),
    async fetchProfile() {
      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_completed_at')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw failure('Reading the profile failed', error);
      return data ?? null;
    },
    upsertCards: (rows) => upsert('cards', rows),
    upsertReviewLogs: (rows) => upsert('review_logs', rows),
    async deleteCards(ids) {
      for (const batch of chunk(ids, BATCH_SIZE)) {
        const { error } = await supabase
          .from('cards')
          .delete()
          .eq('user_id', userId)
          .in('id', batch);
        if (error) throw failure('Removing duplicate cards failed', error);
      }
    },
  };
}

/**
 * Merging two copies of the same card: the newer copy wins as a whole, but translations are
 * unioned (per language the winner's wins), so a language filled in on one device is never lost
 * because the other device's copy of the card was edited later.
 */
function withUnionedTranslations(winner: Card, other: Card): Card {
  const translations = mergeTranslations(other.translations, winner.translations);
  return Object.keys(translations).length > 0 ? { ...winner, translations } : winner;
}

/**
 * Merging two copies of the same card, beyond which copy is newer:
 * - a card whose text is prepared always wins over a pending copy of it (any device can prepare a
 *   pending card; nothing is lost, a pending card has never been reviewed);
 * - "I already know this word" is not lost to a copy that only has newer reviews: when one copy is
 *   retired and the other is not, the retirement stays unless the other copy is the same review
 *   state with the retirement cleared on purpose (the undo), which is newer by definition;
 * - the learner's notes come from the copy whose notes changed last (mergeCardNotes in core).
 */
export function mergeCardCopies(local: Card, remote: Card): Card {
  const localWins = new Date(local.updated_at).getTime() >= new Date(remote.updated_at).getTime();
  let winner = localWins ? local : remote;
  const loser = localWins ? remote : local;

  if (winner.content_status === 'pending' && loser.content_status !== 'pending') {
    winner = { ...loser, queued_at: winner.queued_at ?? loser.queued_at };
  }

  if (loser.retired_at && !winner.retired_at) {
    // The same reviews on both sides, and the winner is newer: the retirement was undone.
    const sameReviews = winner.reps === loser.reps && winner.last_review === loser.last_review;
    if (!sameReviews) winner = { ...winner, retired_at: loser.retired_at };
  }
  // Both copies are asked, as the pending rule above may have swapped which one `winner` is.
  return withUnionedTranslations(mergeCardNotes(mergeCardNotes(winner, local), remote), loser);
}

/**
 * An error sending the same row again would only repeat: the database refused the row itself
 * (SQLSTATE classes 22 and 23, or a row level security refusal).
 */
function refusedByDatabase(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' && (/^2[23]/.test(code) || code === '42501');
}

/**
 * Uploads rows in batches. A row the database refuses must not stop the move for ever, so a
 * refused batch is sent again row by row and the row at fault is left out (after one more try
 * without its deck label, the usual reason: the deck it names is gone). Any other error (no
 * connection, a server error) stops the move; it starts again later.
 */
async function upload(rows: Row[], send: (rows: Row[]) => Promise<void>): Promise<void> {
  for (const batch of chunk(rows, BATCH_SIZE)) {
    try {
      await send(batch);
      continue;
    } catch (err) {
      if (!refusedByDatabase(err)) throw err;
    }
    for (const row of batch) {
      try {
        await send([row]);
      } catch (err) {
        if (!refusedByDatabase(err)) throw err;
        try {
          if (!row.source_deck_id) throw err;
          await send([{ ...row, source_deck_id: null }]);
        } catch (again) {
          if (!refusedByDatabase(again)) throw again;
          console.warn('[LingoPing] The server refused a row of this device:', row.id, again);
        }
      }
    }
  }
}

/** What this device has that the server must get, worked out while nothing else writes. */
interface MergePlan {
  cards: Card[];
  duplicateCardIds: string[];
  knownWords: UserKnownWord[];
  reviewLogs: ReviewLog[];
}

/**
 * The merge of v3.1.0: per card the newer `updated_at` wins (see mergeCardCopies), known words
 * and review logs are unioned by id, duplicate cards for one word are removed on both sides.
 */
async function mergeOnce(userId: string, remote: LegacyRemote): Promise<void> {
  const [remoteCardRows, remoteWordRows, remoteLogRows] = await Promise.all([
    remote.fetchCards(),
    remote.fetchKnownWords(),
    remote.fetchReviewLogs(),
  ]);

  const plan = await StorageManager.locked('data', async (): Promise<MergePlan> => {
    const remoteById = new Map(remoteCardRows.map((row) => [row.id as string, row]));
    const merged: Card[] = [];
    const cardsToUpload: Card[] = [];
    for (const localCard of await StorageManager.getCards()) {
      const remoteRow = remoteById.get(localCard.id);
      if (!remoteRow) {
        merged.push(localCard);
        cardsToUpload.push(localCard);
        continue;
      }
      remoteById.delete(localCard.id);
      const remoteCard = rowToCard(remoteRow);
      const localWins =
        new Date(localCard.updated_at).getTime() >= new Date(remoteCard.updated_at).getTime();
      const winner = mergeCardCopies(localCard, remoteCard);
      merged.push(winner);
      // Upload when this device's copy won, or when the merge added something the server lacks.
      if (
        localWins ||
        !sameTranslations(winner.translations, remoteCard.translations) ||
        (winner.retired_at ?? null) !== (remoteCard.retired_at ?? null) ||
        !sameCardNotes(winner, remoteCard) ||
        winner.content_status !== remoteCard.content_status
      ) {
        cardsToUpload.push(winner);
      }
    }
    for (const remoteRow of remoteById.values()) merged.push(rowToCard(remoteRow));

    // One card per word: duplicates go (the one with more review progress stays), on both sides.
    const { kept, removed } = dedupeCards(merged);
    const removedIds = new Set(removed.map((c) => c.id));
    await StorageManager.saveCards(kept);

    const localWords = await StorageManager.getKnownWords();
    const remoteWordIds = new Set(remoteWordRows.map((row) => row.id));
    const localWordIds = new Set(localWords.map((w) => w.id));
    const remoteOnlyWords = remoteWordRows.filter((row) => !localWordIds.has(row.id as string));
    await StorageManager.saveKnownWords([...remoteOnlyWords.map(rowToKnownWord), ...localWords]);

    const localLogs = await StorageManager.getReviewLogs();
    const remoteLogIds = new Set(remoteLogRows.map((row) => row.id));
    const localLogIds = new Set(localLogs.map((l) => l.id));
    const remoteOnlyLogs = remoteLogRows.filter((row) => !localLogIds.has(row.id as string));
    await StorageManager.saveReviewLogs([...remoteOnlyLogs.map(rowToReviewLog), ...localLogs]);

    return {
      cards: cardsToUpload.filter((c) => !removedIds.has(c.id)),
      duplicateCardIds: removed
        .filter((c) => remoteCardRows.some((r) => r.id === c.id))
        .map((c) => c.id),
      knownWords: localWords.filter((w) => !remoteWordIds.has(w.id)),
      reviewLogs: localLogs.filter((l) => !remoteLogIds.has(l.id)),
    };
  });

  await upload(
    plan.cards.map((c) => cardToRow(c, userId)),
    remote.upsertCards
  );
  if (plan.duplicateCardIds.length > 0) await remote.deleteCards(plan.duplicateCardIds);
  await upload(
    plan.reviewLogs.map((l) => reviewLogToRow(l, userId)),
    remote.upsertReviewLogs
  );

  // Known words go the new way: the server leaves out a word the account has already (under
  // another id, from another device), where a direct upload of the whole list would fail.
  for (const batch of chunk(plan.knownWords, MAX_KNOWN_WORDS_PER_OP)) {
    await StorageManager.enqueue({
      type: 'known.add',
      words: batch.map((w) => ({
        id: w.id,
        lemma: w.lemma,
        language: w.language,
        base_lemma: w.base_lemma ?? null,
        created_at: w.created_at,
      })),
    });
  }
}

/**
 * Settings this device holds that the account does not: a change v3.1.0 had not uploaded yet, a
 * reminder interval or review size the learner chose here (those two were never synced before),
 * and onboarding finished on this device only. They are sent as operations; every other setting
 * comes from the server with the first pull.
 */
async function sendLocalSettings(remote: LegacyRemote): Promise<void> {
  const profile = await StorageManager.getProfile();
  const settings: Extract<SyncOpBody, { type: 'settings.set' }> = { type: 'settings.set' };

  if (await StorageManager.getLocalValue<boolean>(NATIVE_LANGUAGE_UNSYNCED_KEY)) {
    if (isTranslationLanguage(profile.native_language)) {
      settings.native_language = profile.native_language;
    }
  }
  const wholeNumber = (value: unknown, min: number, max: number): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
  if (await StorageManager.getLocalValue<boolean>(NEW_WORDS_UNSYNCED_KEY)) {
    if (wholeNumber(profile.new_words_per_day, 1, 500)) {
      settings.new_words_per_day = profile.new_words_per_day;
    }
  }
  if (
    wholeNumber(profile.session_interval_minutes, 1, 1440) &&
    profile.session_interval_minutes !== 10
  ) {
    settings.session_interval_minutes = profile.session_interval_minutes;
  }
  if (wholeNumber(profile.cards_per_session, 1, 50) && profile.cards_per_session !== 5) {
    settings.cards_per_session = profile.cards_per_session;
  }
  if (await StorageManager.isOnboardingComplete()) {
    const remoteProfile = await remote.fetchProfile();
    if (!remoteProfile?.onboarding_completed_at) settings.onboarding_completed = true;
  }

  if (Object.keys(settings).length > 1) await StorageManager.enqueue(settings);
  await StorageManager.setLocalValue(NATIVE_LANGUAGE_UNSYNCED_KEY, null);
  await StorageManager.setLocalValue(NEW_WORDS_UNSYNCED_KEY, null);
}

/** Whether this device holds anything of the signed-in learner that the server may lack. */
async function hasOwnData(): Promise<boolean> {
  const [cards, words, logs, onboarded] = await Promise.all([
    StorageManager.getCards(),
    StorageManager.getKnownWords(),
    StorageManager.getReviewLogs(),
    StorageManager.isOnboardingComplete(),
  ]);
  return cards.length > 0 || words.length > 0 || logs.length > 0 || onboarded;
}

/**
 * Moves this device over for `userId` if it has not been moved yet. Throws when the move could
 * not be finished (no connection, signed out); the caller tries again later and must not pull
 * from the server before it succeeded: a pull replaces the saved copy, and what only this device
 * has would be gone.
 *
 * A device with nothing of its own (a new installation, the website, after a sign-out) has
 * nothing to merge and is just marked.
 */
export async function moveOverToCloudFirst(
  userId: string,
  remoteFor: (userId: string) => Promise<LegacyRemote> = async (id) =>
    supabaseLegacyRemote(await signedInSupabase(), id)
): Promise<void> {
  const keys = syncKeys(userId);
  if (await StorageManager.getLocalValue<boolean>(keys.movedOver)) return;

  if (await hasOwnData()) {
    const remote = await remoteFor(userId);
    await StorageManager.normalizeKnownWordIds();
    // Cards of the old bundled Basic German deck point at the stored pictures from now on.
    await migrateBundledPictures(userId);
    await mergeOnce(userId, remote);
    await sendLocalSettings(remote);
    console.log('[LingoPing] This device now follows the copy in the cloud.');
  }

  // From here on the server's copy is the real one: forget where the last pull was, so the
  // next one fetches everything, and never come back here.
  await StorageManager.locked('data', async () => {
    await StorageManager.setLocalValue(keys.state, null);
    await StorageManager.setLocalValue(keys.movedOver, true);
  });
}
